import { createStyles, makeStyles } from "@material-ui/core";
import { Variant } from "@material-ui/core/styles/createTypography";
import classnames from "classnames";
import * as React from "react";

import { RelodeTheme, Text } from "..";

const useStyles = makeStyles<RelodeTheme, GroupProps>(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                display: "flex",
                flexDirection: "column",
                marginBottom: theme.spacing(5),
            },
            label: {
                marginBottom: theme.spacing(2),
                textTransform: "lowercase",
            },
            layout: {},
            twoColumn: {
                display: "grid",
                gridTemplateColumns: "300px 1fr",
                gridGap: props => theme.spacing(props.gridGap ?? 2),
            },
            oneColumn: {
                display: "grid",
                gridTemplateColumns: "1fr",
                gridGap: props => theme.spacing(props.gridGap ?? 2),
            },
        }),
    { name: "R_Group" }
);

type GroupCSSProperties = "root" | "label" | "layout";
type GroupStyles = Partial<Record<GroupCSSProperties, string>>;

export interface GroupProps {
    id?: string;
    children?: React.ReactNode;
    classes?: GroupStyles;
    className?: string;
    label?: string;
    layout?: "oneColumn" | "twoColumn" | "custom";
    labelVariant?: Variant | "inherit" | "srOnly";
    gridGap?: number;
    /** component to render instead of children.
     *
     * the component property takes precedence over children.
     * if both are specified children will not render.
     *
     * this can be used to override the default layout of the group control.
     */
    component?: JSX.Element;
}

/**
 * @deprecated
 * use `<Group>` in combination with `<Layout>` instead
 */
export const GroupWithColumns = (props: GroupProps) => {
    const {
        id,
        className: classNameProp,
        label,
        labelVariant = "h4",
        layout = "twoColumn",
        component: Component,
    } = props;

    const classes = useStyles(props);
    const rootClass = classnames(classes.root, classNameProp);
    const renderInner = () => {
        if (!!Component) return Component;

        const layoutClasses = classnames(classes.layout, {
            [classes.twoColumn]: layout === "twoColumn",
            [classes.oneColumn]: layout === "oneColumn",
        });

        return <div className={layoutClasses}>{props.children}</div>;
    };

    const renderLabel = () => {
        if (!!label) {
            return (
                <Text variant={labelVariant} className={classes.label}>
                    {label}
                </Text>
            );
        }
        return null;
    };
    return (
        <div id={id} className={rootClass}>
            {renderLabel()}
            {renderInner()}
        </div>
    );
};
